import isEqual from 'lodash/isEqual'
import React, { useEffect, useState } from 'react'
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import { ContentModal } from './ContentModal'
import { MobileFooter } from './MobileFooter'
import { CurrentFieldName, Inputs } from '../useFieldNavigator'
import { TravelTypeEnum } from '../../../../lib/graphql'
import { Button, Icon } from '../../../atoms'
import { useTravelTypeContext } from '../../../contexts'
import { AdultsPickerModeEnum } from '../../../forms/adults-picker/adults-picker.enum'
import { AdultsSearchBarPicker } from '../../../forms/adults-picker/AdultsSearchBarPicker'
import { BudgetSearchBarPicker } from '../../../forms/budget-picker/BudgetSearchBarPicker'
import { DatePickerModeEnum } from '../../../forms/date-picker/date-picker.enum'
import { DateSearchBarPicker } from '../../../forms/date-picker/DateSearchBarPicker'
import { MobileBackPage } from '../../../layout'
import { IDatesCalendarProps } from '../../../organisms/dates-calendar/dates-calendar'
import { LocationPickerModeEnum } from '../../../organisms/location-picker/location-picker.enum'
import { MostBookedRegionType } from '../../../organisms/location-picker/location-picker.type'
import { LocationSearchBarPicker } from '../../../organisms/location-picker/LocationSearchBarPicker'

type SearchCityMobileStepperProps = {
  control: Control<Inputs>
  isLoading: boolean
  dataMixpanel?: {
    name: string
    data?: any
  }
  onSubmit: () => void
  isOpen: boolean
  onCloseModal: () => void
  onOpenMobileModal: (field: CurrentFieldName) => void
  currentField: CurrentFieldName
  formValues: Inputs
  mostBookedRegions: MostBookedRegionType[]
  dateCalendarProps: IDatesCalendarProps
  onValid: (currentField: keyof Inputs) => () => void
  getAndSetter: {
    setValue: UseFormSetValue<Inputs>
    getValues: UseFormGetValues<Inputs>
  }
  isFromSearchPage: boolean
}

const InputContainer: React.FC<
  React.PropsWithChildren<{
    onClick: React.MouseEventHandler<HTMLButtonElement>
    dataMixpanel: { name: string; data: unknown }
  }>
> = ({ children, onClick }) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick(event)
  }

  return (
    <button
      className='flex h-[58px] items-center rounded-[5px] border border-solid border-grey-500 px-3'
      onClick={handleClick}
    >
      {children}
    </button>
  )
}

const renderTitleModal = (currentField: CurrentFieldName) => {
  switch (currentField) {
    case 'location':
      return (
        <FormattedMessage
          id='ui.LocationPicker.revampMobileTitle'
          defaultMessage='Indiquez votre destination'
        />
      )
    case 'dates':
      return (
        <FormattedMessage
          id='ui.DatePicker.selectDates'
          defaultMessage='Sélectionnez vos dates'
        />
      )
    case 'adults':
      return (
        <FormattedMessage
          id='ui.AdultsPicker.mobileRevampTitle'
          defaultMessage='Nombre de participants'
        />
      )
    case 'budget':
      return (
        <FormattedMessage
          id='ui.BudgetPicker.mobileTitle'
          defaultMessage='Budget'
        />
      )
    default:
      return null
  }
}

export const SearchCityMobileStepper: React.FC<
  SearchCityMobileStepperProps
> = ({
  control,
  isLoading,
  dataMixpanel,
  onSubmit,
  isOpen,
  currentField,
  onCloseModal,
  onOpenMobileModal,
  formValues,
  mostBookedRegions,
  dateCalendarProps,
  onValid,
  getAndSetter,
  isFromSearchPage,
}) => {
  const { travelType } = useTravelTypeContext()
  const [currentTravelType, setCurrentTravelType] =
    useState<TravelTypeEnum>(travelType)

  const commonSearchBarPickerProps = {
    isMobile: true,
    alternativeClassName: 'alternativeAi',
  }

  const isCorporateEveningEvent =
    travelType === TravelTypeEnum.CORPORATE_EVENING_EVENT
  const allowMultiDates =
    travelType === TravelTypeEnum.NIGHTLY_TRIP ||
    travelType === TravelTypeEnum.DAILY_TRIP

  const onClickInput =
    (field: CurrentFieldName) => (event: React.MouseEvent) => {
      event.preventDefault()
      onOpenMobileModal(field)
    }

  const makeDataMixpanel = (name: string) => ({
    name,
    data: { newSearch: true, isSmallDevice: true },
  })

  // This useEffect is used to reset the dates field when the travelType changes
  useEffect(() => {
    if (currentTravelType !== travelType) {
      if (!isEqual(getAndSetter.getValues('dates'), {})) {
        getAndSetter.setValue('dates', {})
      }
    }
    setCurrentTravelType(travelType)
  }, [travelType])

  return (
    <>
      <div className='flex flex-col gap-[10px]'>
        <Controller
          name='location'
          control={control}
          rules={{}}
          render={({ field }) => (
            <InputContainer
              onClick={onClickInput('location')}
              dataMixpanel={makeDataMixpanel('inputLocation')}
            >
              <LocationSearchBarPicker
                mode={LocationPickerModeEnum.HOUSE_SEARCH}
                value={field?.value?.label}
                isLocationSelected={!!field.value?.label}
                revampPicker
                {...commonSearchBarPickerProps}
              />
            </InputContainer>
          )}
        />
        <Controller
          name='dates'
          control={control}
          rules={{}}
          render={({ field }) => (
            <InputContainer
              onClick={onClickInput('dates')}
              dataMixpanel={makeDataMixpanel('date_arrivee_depart')}
            >
              <DateSearchBarPicker
                mode={DatePickerModeEnum.HOUSE_SEARCH}
                fieldValue={field?.value}
                allowMultiDates={allowMultiDates}
                isCorporateEveningEvent={isCorporateEveningEvent}
                revampPicker
                isDatesSelected={
                  !!field.value?.startDate && !!field.value?.endDate
                }
                className='flex items-center gap-1'
                hideLabels
                {...commonSearchBarPickerProps}
              />
            </InputContainer>
          )}
        />
        <Controller
          name='adults'
          control={control}
          rules={{}}
          render={({ field }) => (
            <InputContainer
              onClick={onClickInput('adults')}
              dataMixpanel={makeDataMixpanel('nombre_voyageurs')}
            >
              <AdultsSearchBarPicker
                mode={AdultsPickerModeEnum.HOUSE_SEARCH}
                fieldValue={field?.value}
                revampPicker
                isAdultsSelected={!!field.value}
                className='flex items-center gap-2'
                travelType={travelType}
                {...commonSearchBarPickerProps}
              />
            </InputContainer>
          )}
        />
        <Controller
          name='budget'
          control={control}
          rules={{}}
          render={({ field }) => (
            <InputContainer
              onClick={onClickInput('budget')}
              dataMixpanel={makeDataMixpanel('budget_picker')}
            >
              <BudgetSearchBarPicker<Inputs, 'budget'>
                field={field}
                isFromSearchPage={isFromSearchPage}
                revampPicker
              />
            </InputContainer>
          )}
        />
        <Button
          className={s.searchButtonMobile}
          loading={isLoading}
          disabled={isLoading === true}
          dataMixpanel={dataMixpanel}
          data-testid='search-button'
          onClick={onSubmit}
        >
          <FormattedMessage
            id='ui.SearchCityDatesAdults.search'
            defaultMessage='Rechercher'
          />
        </Button>
      </div>

      {isOpen && (
        <MobileBackPage
          backPageProps={{
            title: <div>{renderTitleModal(currentField)}</div>,
            extra: {
              icon: (
                <Icon
                  name='cross-without-circle'
                  size={16}
                  color='currentColor'
                  className='cursor-pointer'
                />
              ),
              onClick: onCloseModal,
            },
          }}
          isModal
          Footer={
            <MobileFooter
              onClick={onSubmit}
              disabled={!formValues[currentField as keyof Inputs]}
              isLoading={isLoading}
            />
          }
        >
          <ContentModal
            control={control}
            currentField={currentField}
            name={currentField}
            mostBookedRegions={mostBookedRegions}
            dateCalendarProps={dateCalendarProps}
            onValid={onValid}
          />
        </MobileBackPage>
      )}
    </>
  )
}
